import { useState, useRef, useCallback, useEffect } from 'react'
import { createPortal } from 'react-dom'
import api from '../services/api'
import { useListaFlutuante } from '../hooks/useListaFlutuante'

interface Servico {
  pre_cod: string
  pre_des: string
}

interface Props {
  codValue: string
  desValue: string
  onChange: (cod: string, des: string) => void
}

export default function ServicoPrestadoSelect({ codValue, desValue, onChange }: Props) {
  const [aberta, setAberta] = useState(false)
  const [filtro, setFiltro] = useState('')
  const [resultados, setResultados] = useState<Servico[]>([])
  const [buscando, setBuscando] = useState(false)
  const [localCod, setLocalCod] = useState(codValue)
  const [aviso, setAviso] = useState('')
  const ref = useRef<HTMLDivElement>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { posicao, listaRef } = useListaFlutuante(ref, aberta, () => setAberta(false))

  useEffect(() => { setLocalCod(codValue); if (codValue) setAviso('') }, [codValue])

  const buscar = useCallback((valor: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (valor.length < 2) { setResultados([]); return }
    debounceRef.current = setTimeout(async () => {
      setBuscando(true)
      try {
        const r = await api.get('/erp/servicosprestados', { params: { busca: valor } })
        setResultados(r.data)
      } finally {
        setBuscando(false)
      }
    }, 350)
  }, [])

  async function buscarPorCodigo(cod: string) {
    if (!cod.trim()) { onChange('', ''); setAviso(''); return }
    try {
      const r = await api.get('/erp/servicosprestados', { params: { busca: cod.trim() } })
      const encontrado = r.data.find((s: Servico) => s.pre_cod.trim() === cod.trim())
      if (encontrado) {
        onChange(encontrado.pre_cod, encontrado.pre_des)
        setLocalCod(encontrado.pre_cod)
        setAviso('')
      } else {
        onChange('', '')
        setAviso(`Código "${cod.trim()}" não encontrado`)
      }
    } catch {
      onChange('', '')
      setAviso(`Código "${cod.trim()}" não encontrado`)
    }
  }

  function selecionar(s: Servico) {
    onChange(s.pre_cod, s.pre_des)
    setLocalCod(s.pre_cod)
    setAviso('')
    setAberta(false)
    setFiltro('')
    setResultados([])
  }

  return (
    <div className="nat-wrap" ref={ref}>
      <div className="nat-input-row">
        <input
          className="nat-cod-input-lg"
          type="text"
          placeholder="Código"
          value={localCod}
          style={aviso ? { borderColor: '#dc2626' } : undefined}
          onChange={e => { setLocalCod(e.target.value); setAviso('') }}
          onBlur={e => {
            if (e.target.value) buscarPorCodigo(e.target.value)
            else { onChange('', ''); setAviso('') }
          }}
          onKeyDown={e => { if (e.key === 'Enter') buscarPorCodigo(localCod) }}
        />
        <div className="nat-desc-box" onClick={() => { setAberta(o => !o); setFiltro(''); setResultados([]) }}>
          {desValue
            ? <span className="nat-dsc">{desValue}</span>
            : <span className="nat-placeholder">Selecione o serviço prestado...</span>
          }
          <span className="nat-chevron">{aberta ? '▴' : '▾'}</span>
        </div>
      </div>
      {aviso && <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '0.2rem', paddingLeft: '0.25rem' }}>{aviso}</div>}

      {aberta && posicao && createPortal(
        <div ref={listaRef} className="nat-lista" style={{ position: 'fixed', ...posicao, minWidth: posicao.width }}>
          <div className="nat-filtro-wrap">
            <input
              autoFocus
              className="nat-filtro"
              placeholder="Código ou descrição..."
              value={filtro}
              onChange={e => { setFiltro(e.target.value); buscar(e.target.value) }}
            />
          </div>
          <div className="nat-itens">
            {buscando && <div className="nat-vazio">Buscando...</div>}
            {!buscando && filtro.length >= 2 && resultados.length === 0 && (
              <div className="nat-vazio">Nenhum resultado</div>
            )}
            {!buscando && filtro.length < 2 && (
              <div className="nat-vazio" style={{ color: '#9ca3af' }}>Digite ao menos 2 caracteres</div>
            )}
            {resultados.map(s => (
              <button
                key={s.pre_cod}
                className={`nat-item${s.pre_cod === codValue ? ' nat-item--ativo' : ''}`}
                onClick={() => selecionar(s)}
              >
                <div className="nat-item-linha1">
                  <span className="nat-item-cod">{s.pre_cod}</span>
                </div>
                <div className="nat-item-dsc">{s.pre_des}</div>
              </button>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}
